import { Injectable } from 'angular-ts-decorators';

import { Moment } from '@shared/app/scripts/vendor/moment';
import { PeriodType } from '@core/app/scripts/services/period-type';
import { DateSource } from '@shared/app/scripts/services/date-source';
import { includes, keysIn } from 'lodash';

/**
 * Service in the coreModule. It allows to normalize the dates that
 * are provided in the route params, and update the address bar accordingly.
 */
@Injectable('routeNormalizer')
export class RouteNormalizer {
  constructor(
    private $routeParams: ng.route.IRouteParamsService,
    private $route: ng.route.IRouteService,
    private $location: ng.ILocationService,
    private dateSource: DateSource,
    private periodType: PeriodType,
    private routeDateFormat: string,
    private moment: Moment,
  ) {
    'ngInject';
  }

  /**
   * Method that checks the routeParams values, and enforce that there is
   * meaningful period chosen. It defaults 2 route arguments:
   * * periodType - periodType is read. It'll be enforced
   * to have one of the values of {@link coreModule.periodType
   * periodType}. By default set to 'day'
   * * periodDate - date from anywhere inside period. By default set to today.
   * @return {boolean}
   * Returns true if the parameters are update & route updated. The
   * controller should give up at that point & wait to be destoyed by
   * redirection
   */
  defaultPeriodParams() {
    let periodMoment = this.moment(this.$routeParams.periodDate),
      defaulted = false;

    if (
      !periodMoment.isValid() ||
      !angular.isDefined(this.$routeParams.periodDate)
    ) {
      periodMoment = this.moment(this.dateSource.now());
    }

    if (this.$routeParams.periodType === this.periodType.quarter) {
      periodMoment = periodMoment.startOf(this.periodType.quarter);
    }

    // update $routeParams in case if parsed date difference from the route value
    if (
      this.$routeParams.periodDate !== periodMoment.format(this.routeDateFormat)
    ) {
      this.$routeParams.periodDate = periodMoment.format(this.routeDateFormat);
      defaulted = true;
    }

    // default to 'day' if period is missing
    if (!includes(this.periodType, this.$routeParams.periodType)) {
      this.$routeParams.periodType = this.periodType.day;
      defaulted = true;
    }

    if (this.$routeParams.periodType === this.periodType.quarter) {
      periodMoment = periodMoment.startOf(this.periodType.quarter);
    }

    if (defaulted) {
      this.$route.updateParams(this.$routeParams);
    }

    return defaulted;
  }

  /**
   * Method that checks the routeParams values, and enforce that there is
   * meaningful compare period chosen. It defaults one param:
   * * comparePeriodDate - it'll be enforced to be a well formatted date, and
   * defaulted a year before periodDate if missing
   * @return {boolean}
   * Returns true if the parameter are update & route updated. The
   * controller should give up at that point & wait to be destroyed by
   * redirection
   */
  defaultComparePeriod() {
    let defaulted = false;

    const compareMoment = this.moment(
      this.$routeParams.comparePeriodDate,
      this.routeDateFormat,
    );

    if (!compareMoment.isValid()) {
      let periodMoment = this.moment(
        this.$routeParams.periodDate,
        this.routeDateFormat,
      );

      if (!periodMoment.isValid()) {
        periodMoment = this.moment(this.dateSource.now());
      }

      this.$routeParams.comparePeriodDate = periodMoment
        .subtract(1, 'year')
        .format(this.routeDateFormat);

      defaulted = true;
    }

    if (defaulted) {
      this.$route.updateParams(this.$routeParams);
    }

    return defaulted;
  }

  /**
   * Method that checks if the giver parameters are valid dates
   * And returns a object of a begin and endate
   * @param {string} startYear
   * A string that consist of a year.
   * This will be used as the start year for the compare object
   * @param {string} endYear
   * A string that consist of a year
   * This will be used as the end year for the compare object
   * @return {boolean}
   * Returns true if the parameters are update & route updated. The
   * controller should give up at that point & wait to be destoyed by
   * redirection
   */
  defaultCompareParams() {
    let startYearMoment = this.moment(this.$routeParams.startYear, 'YYYY'),
      endYearMoment = this.moment(this.$routeParams.endYear, 'YYYY'),
      defaulted = false;

    if (!startYearMoment.isValid()) {
      this.$routeParams.startYear = this.moment(this.dateSource.now())
        .subtract(1, 'years')
        .startOf('year')
        .format('YYYY');
    }

    if (!endYearMoment.isValid()) {
      this.$routeParams.endYear = this.moment(this.dateSource.now())
        .startOf('year')
        .format('YYYY');
      defaulted = true;
    }

    if (defaulted) {
      this.$route.updateParams(this.$routeParams);
    }

    return defaulted;
  }

  /**
   * Method that checks the routeParams values, and enforce that there is
   * meaningful date set.
   * @param {string} dateParam
   * Date. By default set to today.
   * @return {boolean}
   * Returns true if the parameters are update & route updated. The
   * controller should give up at that point & wait to be destoyed by
   * redirection
   */
  defaultDate(dateParam) {
    let periodMoment = this.moment(
        this.$routeParams[dateParam],
        this.routeDateFormat,
      ),
      defaulted = false;

    if (!periodMoment.isValid()) {
      this.$routeParams[dateParam] = this.moment(this.dateSource.now()).format(
        this.routeDateFormat,
      );
      defaulted = true;
    } else if (
      this.$routeParams[dateParam] !== periodMoment.format(this.routeDateFormat)
    ) {
      // update $routeParams in case if parsed date difference from the route value
      this.$routeParams[dateParam] = periodMoment.format(this.routeDateFormat);
      defaulted = true;
    }

    if (defaulted) {
      this.$route.updateParams(this.$routeParams);
    }

    return defaulted;
  }

  /**
   * Method that checks the routeParams values, and enforce that there is
   * meaningful category for turnover
   * @return {boolean}
   * Returns true if the parameters are update & route updated. The
   * controller should give up at that point & wait to be destoyed by
   * redirection
   */
  defaultTurnoverCategory() {
    let defaulted = false;

    if (
      !includes(
        ['article', 'brand', 'articleGroup', 'articleColorSize'],
        this.$routeParams.category,
      )
    ) {
      this.$routeParams.category = 'article';
      defaulted = true;
    }

    if (defaulted) {
      this.$route.updateParams(this.$routeParams);
    }

    return defaulted;
  }

  /**
   * Method that checks the $routeParams values, and adds provided
   * defaults for every missing one.
   * @param {object} defaults
   * Default values to be used. Object with string or array of strings in each
   * key.
   * @return {boolean}
   * Returns true if the parameters are updated & route updated. The
   * controller should give up at that point & wait to be destroyed by
   * redirection
   */
  defaultParams(defaults: { [key in string]: string | string[] }) {
    if (this.$routeParams.defaultParams) {
      this.$routeParams.defaultParams = undefined;
    } else {
      return false;
    }

    if (!angular.isObject(defaults)) {
      return false;
    }

    for (let key of keysIn(defaults)) {
      if (!angular.isDefined(this.$routeParams[key])) {
        if (typeof defaults[key] === 'string') {
          this.$routeParams[key] = defaults[key];
        } else if (angular.isArray(defaults[key])) {
          this.$routeParams[key] = (<string[]>defaults[key]).join();
        }
      }
    }

    this.$route.updateParams(this.$routeParams);

    return true;
  }

  /**
   * Method that checks the $routeParams values, and enforce that there is
   * meaningful start/end chosen. Can cover cases where one of the two
   * dates is missing or both
   * @param {string} periodTypeValue
   * Interval for which dates should be defaulted
   * @return {boolean}
   * Returns true if the parameters are update & route updated. The
   * controller should give up at that point & wait to be destoyed by
   * redirection
   */
  defaultStartEndDates(periodTypeValue) {
    let defaulted = false;

    let startMoment = angular.isDefined(this.$routeParams.start)
        ? this.moment(this.$routeParams.start)
        : undefined,
      endMoment = angular.isDefined(this.$routeParams.end)
        ? this.moment(this.$routeParams.end)
        : undefined;

    if (!startMoment && !endMoment) {
      this.$routeParams.start = this.moment(this.dateSource.now())
        .subtract(1, periodTypeValue)
        .format(this.routeDateFormat);
      this.$routeParams.end = this.moment(this.dateSource.now()).format(
        this.routeDateFormat,
      );
      defaulted = true;
    } else if (!startMoment) {
      this.$routeParams.start = this.moment(this.$routeParams.end)
        .subtract(1, periodTypeValue)
        .format(this.routeDateFormat);
      defaulted = true;
    } else if (!endMoment) {
      this.$routeParams.end = this.moment(this.$routeParams.start)
        .add(1, periodTypeValue)
        .format(this.routeDateFormat);
      defaulted = true;
    }

    if (defaulted) {
      this.$location.replace();
      this.$route.updateParams(this.$routeParams);
    }

    return defaulted;
  }

  /**
   * Method that checks the $routeParams values, and enforce that there is
   * meaningful end chosen.
   * @return {boolean}
   * Returns true if the parameters are update & route updated. The
   * controller should give up at that point & wait to be destoyed by
   * redirection
   */
  defaultEnd(): boolean {
    let defaulted = false;

    let endMoment = angular.isDefined(this.$routeParams.end)
      ? this.moment(this.$routeParams.end)
      : undefined;

    if (!endMoment) {
      this.$routeParams.end = this.moment(this.dateSource.now()).format(
        this.routeDateFormat,
      );
      defaulted = true;
    }

    if (defaulted) {
      this.$route.updateParams(this.$routeParams);
    }

    return defaulted;
  }
}
